import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { blogPosts } from '../data/blogData';

const BlogDetailPage = ({ onBookingClick }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.slug === slug);
  
  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
          <button onClick={() => navigate('/blog')} className="text-amber-500 hover:underline">
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  // Get related posts (same category, exclude current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Image */}
      <section className="relative h-[60vh] min-h-[500px] bg-black">
        <img 
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
        
        {/* Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            {/* Back Button - Left Corner */}
            <div className="absolute top-8 left-8">
              <button
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 text-white hover:text-amber-500 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Blog</span>
              </button>
            </div>

            {/* Centered Content */}
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block bg-amber-500 text-black px-4 py-2 text-sm font-bold uppercase mb-4">
                {post.category}
              </span>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {post.title}
              </h1>
              
              <div className="flex items-center justify-center gap-6 text-white/80">
                <span className="flex items-center gap-2">
                  <Calendar size={18} />
                  {post.date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={18} />
                  {post.readTime}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Share Button */}
            <div className="flex justify-end mb-8">
              <button className="flex items-center gap-2 text-gray-600 hover:text-amber-500 transition-colors">
                <Share2 size={20} />
                <span className="font-semibold">Share Article</span>
              </button>
            </div>
            
            {/* Article Body */}
            <div className="prose prose-lg max-w-none blog-content">
              <div 
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* CTA Section */}
            <div className="mt-16 bg-amber-50 p-8 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-black mb-4">
                Ready to Experience Professional Grooming?
              </h3>
              <p className="text-gray-700 mb-6">
                Visit LookWell Gents Salon for expert services tailored to your style
              </p>
              <button
                onClick={onBookingClick}
                className="bg-amber-500 text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors"
              >
                Book Your Appointment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold text-black mb-8 text-center">
                Related Articles
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <article key={relatedPost.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-amber-500 text-black px-3 py-1 text-xs font-bold uppercase">{relatedPost.category}</span>
                        </div>
                      </div>
                    </Link>
                    <div className="p-6">
                      <p className="text-gray-500 text-sm mb-2">{relatedPost.date}</p>
                      <Link to={`/blog/${relatedPost.slug}`}>
                        <h3 className="text-lg font-bold text-black mb-2 hover:text-amber-500 transition-colors">
                          {relatedPost.title}
                        </h3>
                      </Link>
                      <Link 
                        to={`/blog/${relatedPost.slug}`}
                        className="inline-flex items-center text-amber-500 font-semibold hover:text-amber-600 transition-colors text-sm"
                      >
                        Read More 
                        <span className="ml-2">→</span>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
